import { useLife } from '@/state/life';
import { useMarkets } from '@/state/markets';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { addImpulse } from './prices';
import { PHONE_ACTION_HANDLERS } from './phoneActions';

/**
 * Trump Rally Stand-In mini-arc.
 *
 * Triggered when player accepts the Senator's "donor event" invite
 * (which is, of course, a rally). 3 moments at the rally — what gesture
 * do you make on camera as the cameras pan to the people behind the
 * podium. Choices have real consequences across tribal rep + notoriety.
 */

interface Moment {
  text: string;
  options: { label: string; effect: () => void }[];
}

const MOMENTS: Moment[] = [
  {
    text: 'STAGE PRODUCER: "the cameras are coming to your section. stand. clap. don\'t make eye contact with anyone behind you."',
    options: [
      { label: 'clap politely + smile', effect: () => {
        usePower.getState().bumpRep('maga', 6);
        usePower.getState().bumpNotoriety(5);
      }},
      { label: 'pump fists. yell.', effect: () => {
        usePower.getState().bumpRep('maga', 12);
        usePower.getState().bumpRep('left', -10);
        usePower.getState().bumpNotoriety(10);
      }},
      { label: 'discreetly check your phone', effect: () => {
        usePower.getState().bumpRep('press', 4);
        useLife.getState().bumpSanity(1);
      }},
    ],
  },
  {
    text: 'TRUMP: "we have some incredible people here tonight. some real winners. winners that you would not believe."',
    options: [
      { label: 'mouth "thank you, sir"', effect: () => {
        usePower.getState().bumpRep('maga', 10);
      }},
      { label: 'wave at the camera', effect: () => {
        usePower.getState().bumpNotoriety(8);
      }},
      { label: 'do nothing. this is fine.', effect: () => {
        useLife.getState().bumpSanity(2);
      }},
    ],
  },
  {
    text: 'A PROTESTOR breaks into the section. SECURITY hesitates. cameras are rolling.',
    options: [
      { label: 'tackle the protestor', effect: () => {
        usePower.getState().bumpRep('maga', 18);
        usePower.getState().bumpRep('left', -18);
        useLife.getState().bumpFbiHeat(8);
        useLife.getState().bumpSanity(-8);
        usePower.getState().bumpNotoriety(20);
      }},
      { label: 'film it', effect: () => {
        usePower.getState().bumpNotoriety(12);
        useLife.getState().bumpSanity(-2);
      }},
      { label: 'try to de-escalate', effect: () => {
        usePower.getState().bumpRep('press', 12);
        usePower.getState().bumpRep('left', 8);
        useLife.getState().bumpSanity(4);
      }},
    ],
  },
];

export function startRallyArc(): void {
  useMeta.getState().recordLore('arc-rally-stand-in', 'Rally Stand-In');
  useCinematic.getState().fire({
    id: `rally-${Date.now()}`,
    type: 'breaking',
    headline: 'YOU ARE AT THE RALLY.',
    subhead: 'cameras everywhere. teleprompter is decorative. behind the podium is where reputations are made and ended.',
    durationMs: 5000,
  });
  sounds.notifyAlert();

  // Trump bump on certain tickers from the rally happening
  addImpulse({ ticker: 'TARI', startMs: Date.now(), durationMs: 25 * 60_000, logDelta: 0.04 });
  addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 25 * 60_000, logDelta: 0.025 });

  for (let i = 0; i < MOMENTS.length; i++) {
    const m = MOMENTS[i]!;
    setTimeout(() => {
      const ids = ['a', 'b', 'c'];
      const actions = m.options.map((opt, idx) => ({
        id: ids[idx]!,
        label: opt.label,
        effectId: `rally-q${i}-${ids[idx]}`,
      }));
      for (let j = 0; j < m.options.length; j++) {
        const id = `rally-q${i}-${ids[j]}`;
        const opt = m.options[j]!;
        PHONE_ACTION_HANDLERS[id] = () => {
          opt.effect();
          sounds.fill();
        };
      }
      usePhone.getState().receiveMessage({
        threadId: 'senator',
        fromNpc: 'senator',
        text: m.text,
        actions,
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: `at the rally: moment ${i + 1}/3` });
      sounds.notifyAlert();
    }, 5_000 + i * 30_000);
  }

  // Aftermath
  setTimeout(() => {
    useXClone.getState().receivePost({
      authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
      text: 'BREAKING: a man identified by Twitter sleuths as a "well-known retail trader" was clearly visible behind the podium tonight. analysts: "we cannot say what this means but we will."',
      likes: 22000, reposts: 6800,
    });
    useMarkets.getState().addCash(-2_000); // donor "cost"
  }, 5_000 + MOMENTS.length * 30_000 + 5_000);
}
